import { z } from "zod";

import { enumReadSchema } from "../../../../shared/core/enum-read.schema";
import {
  VibeGitProjectDefaultBranchSchema,
  VibeGitProjectDescriptionSchema,
  VibeGitProjectNameSchema
} from "./core";
import { VibeGitProjectStatusReadSchema } from "./status-read.schemas";

/**
 * Lifecycle of a Vibe git project. Mirrors the Prisma enum
 * `VibeGitProjectStatus` exactly — keeping a hand-rolled Zod enum (rather
 * than `z.nativeEnum($Enums.VibeGitProjectStatus)`) avoids pulling
 * Prisma's runtime into `@nexus/types`, which is consumed by the frontend.
 */
export const VibeGitProjectStatusSchema = z.enum(["PENDING", "READY", "FAILED", "ARCHIVED"]);
export type VibeGitProjectStatusValue = z.infer<typeof VibeGitProjectStatusSchema>;

/**
 * Whether `gitRemoteUrl` names something a build can clone RIGHT NOW.
 *
 *   - `available`: it does — the project's own remote, or its repo on the
 *     tenant's git host at the host's current address
 *   - `none`: the project has no remote at all
 *   - `git_host_unavailable`: the repo lives on the tenant's git host, and that
 *     host has no live address (unhealthy, or its address is unreadable). The
 *     URL is withheld rather than answered with the last address the host had,
 *     which may now belong to another machine. Builds wait; they are not failed.
 *
 * Read leniently: a state a newer backend adds reads as its own word.
 */
export const VIBE_GIT_REMOTE_STATES = ["available", "none", "git_host_unavailable"] as const;
export const VibeGitRemoteStateReadSchema = enumReadSchema(VIBE_GIT_REMOTE_STATES);
export type VibeGitRemoteStateReadValue = z.infer<typeof VibeGitRemoteStateReadSchema>;

export const VibeGitProjectSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  /// Org-unique human-readable name; doubles as the Forgejo repo name.
  name: z.string(),
  description: z.string().nullable(),
  /// Default branch of the materialized Forgejo repo. Independent of any
  /// app's deployBranch — N apps may watch N branches of one project.
  defaultBranch: z.string(),
  /// Empty string until the provisioner worker has set up the S3 prefix.
  s3Prefix: z.string(),
  /// Empty string until the per-tenant Vault path is established.
  hookSecretRef: z.string(),
  /// Git remote the build executor clones at the trigger sha — null whenever
  /// `gitRemoteState` is not `available`. A repo on the tenant's git host is
  /// answered at the host's CURRENT in-VPC address, never a stored one.
  gitRemoteUrl: z.string().nullable(),
  /// Why `gitRemoteUrl` is or is not set. Optional only for deploy skew: a
  /// backend that predates it omits it, and every current backend sends it.
  gitRemoteState: VibeGitRemoteStateReadSchema.optional(),
  status: VibeGitProjectStatusReadSchema,
  createdByUserId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type VibeGitProjectDto = z.infer<typeof VibeGitProjectSchema>;

/**
 * App-scoped git-project responses carry the project under BOTH keys:
 * `gitProject` (canonical) and `repository` (deprecated alias — published
 * CLIs built against the pre-decoupling contract read `.repository`;
 * dropped once those CLI generations rotate out).
 *
 * Deploy-skew tolerance (a client shipped from this contract can face a
 * backend that predates it — Vercel deploys before Porter): `gitProject`
 * is optional, and the `repository` slot relaxes the three fields a
 * pre-decoupling backend never sent (`name`, `description`,
 * `defaultBranch`). The parse must degrade, not fail — consumers read
 * `gitProject ?? repository`. The current backend always sends both keys
 * fully populated.
 */
const VibeGitProjectEnvelopeSchema = z.object({
  gitProject: VibeGitProjectSchema.optional(),
  repository: VibeGitProjectSchema.partial({
    name: true,
    description: true,
    defaultBranch: true
  })
});

export const ProvisionVibeGitProjectPathVarsSchema = z.object({
  appId: z.string().uuid()
});

/**
 * Provision body. `gitRemoteUrl` is optional — the clone source for the
 * build executor. Accepts any reachable remote (https / `file://` / local
 * path / ssh), so not constrained to `z.string().url()`. Omitted when the
 * source isn't known yet (set later once the git server provisions it).
 */
export const ProvisionVibeGitProjectBodySchema = z.object({
  gitRemoteUrl: z.string().min(1).max(2048).optional()
});

export const ProvisionVibeGitProjectResponseSchema = VibeGitProjectEnvelopeSchema;

export const GetVibeGitProjectPathVarsSchema = z.object({
  appId: z.string().uuid()
});

export const GetVibeGitProjectResponseSchema = VibeGitProjectEnvelopeSchema;

export const ReprovisionVibeGitProjectPathVarsSchema = z.object({
  appId: z.string().uuid()
});

export const ReprovisionVibeGitProjectResponseSchema = VibeGitProjectEnvelopeSchema;

// ---------------------------------------------------------------------------
// Standalone git-project lifecycle — the primitive addressed on its own,
// with no app in the picture. A project created here has zero apps: its
// refs advance on push and nothing ever deploys, which is the point (a
// pure code store). Apps attach to it separately.
//
// These responses carry the project under `gitProject` only — no
// `repository` alias key. The alias exists solely for CLI/SPA generations
// built before the decoupling, and no such client can call routes that
// did not exist then.
// ---------------------------------------------------------------------------

/**
 * Create body. `name` is org-unique and becomes the Forgejo repo name.
 * `defaultBranch` defaults to `main` server-side when omitted (matching the
 * column default), so the minimal create is `{ name }`.
 *
 * `gitRemoteUrl` is accepted for symmetry with the app-scoped provision
 * path — the clone source for the build executor. Accepts any reachable
 * remote (https / `file://` / local path / ssh), so it is not constrained
 * to `z.string().url()`. Normally omitted: the tenant agent reports the
 * real Forgejo remote when it materializes the repo.
 */
export const CreateVibeGitProjectBodySchema = z.object({
  name: VibeGitProjectNameSchema,
  description: VibeGitProjectDescriptionSchema.optional(),
  defaultBranch: VibeGitProjectDefaultBranchSchema.optional(),
  gitRemoteUrl: z.string().min(1).max(2048).optional()
});

/** Addresses a project by its own id — every standalone route keyed on one. */
export const VibeGitProjectIdPathVarsSchema = z.object({
  id: z.string().uuid()
});

/** The single-project response every standalone route returns. */
export const SingleVibeGitProjectResponseSchema = z.object({
  gitProject: VibeGitProjectSchema
});

export const CreateVibeGitProjectResponseSchema = SingleVibeGitProjectResponseSchema;

export const ListVibeGitProjectsResponseSchema = z.object({
  gitProjects: z.array(VibeGitProjectSchema)
});

export const GetVibeGitProjectByIdPathVarsSchema = VibeGitProjectIdPathVarsSchema;

export const GetVibeGitProjectByIdResponseSchema = SingleVibeGitProjectResponseSchema;

export const ReprovisionVibeGitProjectByIdPathVarsSchema = VibeGitProjectIdPathVarsSchema;

export const ReprovisionVibeGitProjectByIdResponseSchema = SingleVibeGitProjectResponseSchema;

export const DeleteVibeGitProjectPathVarsSchema = VibeGitProjectIdPathVarsSchema;

/**
 * Soft-delete response. Echoes the id whose row was stamped `deletedAt` /
 * ARCHIVED and whose name was freed — mirrors `DeleteVibeAppResponseSchema`.
 */
export const DeleteVibeGitProjectResponseSchema = z.object({
  deletedId: z.string().uuid()
});

// ---------------------------------------------------------------------------
// Attach — the app-scoped route that joins an app to an EXISTING project.
//
// Lives down here, below the standalone block, only because it answers with
// `SingleVibeGitProjectResponseSchema` and a `const` is not hoisted. It
// belongs conceptually beside `provision` above.
// ---------------------------------------------------------------------------

/**
 * Attach an EXISTING standalone git project to an app — the edge that was
 * missing from the graph.
 *
 * `provision` is the other direction: it MINTS a project named after the app.
 * That is right when the app has no code yet and wrong the moment a project
 * already holds the code, and there was previously no way to say "this app
 * deploys from that project". So an app created before its code store could
 * never be joined to it, and a push to the unattached project advanced its
 * refs and deployed nothing — silently, because the push fan-out only reaches
 * apps LINKED to the project.
 *
 * Semantics:
 *   - Idempotent when the app is already attached to THIS project (200).
 *   - 409 when it is attached to a DIFFERENT one. Re-pointing a live app at
 *     another code store is deliberately NOT this route's job: it would have
 *     to answer what happens to in-flight deployments and to the ref history
 *     the old project owns. That gets its own verb when a real case appears —
 *     an unanswered question is worse here than a missing feature.
 *   - 404 when either the app or the project is not in the caller's org, so
 *     cross-tenant probing yields nothing.
 *   - Project status is NOT gated. A PENDING project is still materializing on
 *     the tenant git host, and attaching early is legitimate — the deploy path
 *     already refuses a project with no remote, and re-deciding that here would
 *     only add a second place to disagree.
 */
export const AttachVibeGitProjectPathVarsSchema = z.object({
  appId: z.string().uuid()
});

export const AttachVibeGitProjectBodySchema = z.object({
  gitProjectId: z.string().uuid()
});

/**
 * Carries the project under `gitProject` only — no `repository` alias. That
 * alias exists solely for CLI/SPA generations built before the git/app
 * decoupling, and no such client can call a route that did not exist then.
 */
export const AttachVibeGitProjectResponseSchema = SingleVibeGitProjectResponseSchema;
